import { useEffect, useState } from 'react';
import { getPjcAb } from '@/lib/api';

// [PJC_COBRADO 21/09] Painel do A/B ".pjc cobrado à parte" — metodologia pré-registrada
// no router (admin-api/routers/pjc_ab.py). Lê tudo de um endpoint; sem filtro de data
// (a janela é "desde o flip", fixa por desenho).
// [08/10] PRIMÁRIA passou a ser créditos consumidos/org (D+7 e D+14). Receita caiu para
// secundária e vem sempre em duas visões (winsor p99 | sem top-1), porque com 6
// compradores no experimento inteiro ela inverteu de sinal em 48h. Bloco DECISÃO novo
// com a regra sequencial pré-registrada (leitura oficial 21/11).

interface Braco {
  n: number; n_maduras_14: number; n_maduras_7: number;
  receita_total_brl: number; receita14_por_org: number | null; receita14_por_org_winsor: number | null;
  receita14_por_org_sem_top1: number | null; compradores_maduros14: number;
  receita_todas_por_org: number | null; receita_todas_por_org_winsor: number | null; receita_todas_por_org_sem_top1: number | null;
  compraram14: number; taxa_compra14: number | null; compraram_total: number;
  ativadas7: number; taxa_ativacao7: number | null;
  casos: number; concluidos: number; casos_pjc_baixado: number; casos_pdf_baixado: number;
  taxa_pjc_por_concluido: number | null; pjc_pagos: number; pjc_estornos: number; estornos: number;
  ev_confirmou: number; ev_cancelou: number; ev_sem_saldo: number; pede_humano: number; msgs_chat: number;
  casos_confirmou: number; casos_cancelou: number; casos_cancelou_sem_voltar: number; orgs_confirmou: number; orgs_cancelou: number;
  consumo7_por_org: number | null; consumo7_mediana: number | null; consumo7_maduras: number;
  consumo14_por_org: number | null; consumo14_mediana: number | null; consumo14_maduras: number;
  zeraram: number; taxa_zerou: number | null; dias_ate_zero_mediana: number | null;
}
interface Bayes { p_b_maior: number | null; expected_loss_b: number | null; expected_loss_a: number | null }
interface Boot {
  diff: number | null; ic95: [number, number] | null; p_b_maior: number | null;
  expected_loss_b?: number | null; expected_loss_a?: number | null;
  winsor?: Boot | null; sem_top1?: Boot | null; ilegivel?: boolean; motivo?: string | null; vies?: string;
}
interface Criterio {
  metrica: string; unidade: string; limiar: number;
  expected_loss_a: number | null; expected_loss_b: number | null; expected_loss_pior: number | null;
  atingido: boolean; faltam: number | null;
}
interface Decisao {
  leitura_oficial: string; dias_para_leitura_oficial: number;
  estado: string; pode_decidir: boolean; criterio_bayes_atingido: boolean;
  criterios: Criterio[];
  guardrail: { metrica: string; controle: number | null; teste: number | null; queda_relativa: number | null; p_controle_maior: number | null; tolerancia: string; estourado: boolean };
  criterio_lider_atingido?: boolean; nota_regra?: string;
  regra: string; compradores_total: number; min_compradores_receita: number;
  aviso_receita: string; decisao_final: string;
}
interface Comprador { org: number; data: string; brl: number; n_compras: number; madura_14: boolean; comprou_em_d14: boolean }
interface Data {
  disponivel: boolean; erro?: string;
  flip_at: string; dias_desde_flip: number; orgs_por_dia: number | null;
  pre_registro: { unidade: string; primaria: string; primaria_curta?: string; primaria_trocada_em?: string; secundarias: string[]; guardrails: string[]; sanidade: string; regra_de_parada: string; decisao?: string };
  srm: { n_a: number; n_b: number; chi2: number | null; p: number | null; ok: boolean | null };
  controle: Braco; teste: Braco;
  decisao: Decisao;
  compradores: { controle: Comprador[]; teste: Comprador[] };
  inferencia: { receita14: Boot; receita_todas: Boot; taxa_compra14: Bayes; ativacao7: Bayes; pjc_por_concluido: Bayes; consumo7?: Boot; consumo14?: Boot; zerou?: Bayes };
  poder: { baseline_taxa_compra: number; n_maduras_min: number; mde_abs_agora: number | null; mde_rel_agora: number | null; n_alvo_por_braco: number | null; dias_estimados_para_alvo: number | null };
  atribuicao: { pjc_cobrado: boolean; source: string; n: number }[];
  orgs: any[];
}

const pct = (v: number | null | undefined, d = 1) => (v == null ? '—' : `${(v * 100).toFixed(d)}%`);
const brl = (v: number | null | undefined) => (v == null ? '—' : `R$ ${v.toFixed(2).replace('.', ',')}`);
const num = (v: number | null | undefined) => (v == null ? '—' : String(v));
// variação relativa com sinal explícito: "+31%" não pode ser lido como queda de 31%
const pctRel = (v: number | null | undefined, d = 1) => (v == null ? '—' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(d)}%`);

function Verdict({ p }: { p: number | null }) {
  if (p == null) return <span className="text-slate-400 text-xs">sem dados</span>;
  const cls = p >= 0.95 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : p <= 0.05 ? 'text-red-700 bg-red-50 border-red-200'
    : 'text-slate-600 bg-slate-50 border-slate-200';
  const txt = p >= 0.95 ? 'teste ganha' : p <= 0.05 ? 'teste perde' : 'inconclusivo';
  return <span className={`inline-block rounded border px-2 py-0.5 text-xs font-semibold ${cls}`}>P(teste&gt;controle) {pct(p, 0)} · {txt}</span>;
}

// Inferência de métrica contínua: Δ, IC95, P(teste>controle) e o expected loss nos DOIS
// sentidos — é esse par que a regra de parada lê (ver bloco Decisão).
function BootInf({ b, fmt = num }: { b?: Boot | null; fmt?: (v: number | null | undefined) => string }) {
  if (!b || b.diff == null) return <span className="text-xs text-slate-400">sem amostra suficiente</span>;
  return (
    <span className="text-xs">
      Δ {fmt(b.diff)} · IC95 [{fmt(b.ic95?.[0])}, {fmt(b.ic95?.[1])}] · <Verdict p={b.p_b_maior} />
      {b.expected_loss_b != null && (
        <span className="ml-1 text-slate-500">
          · perda esperada se escolher <b>cobrar</b> {fmt(b.expected_loss_b)} / <b>grátis</b> {fmt(b.expected_loss_a)}
        </span>
      )}
    </span>
  );
}

export function PjcAbPage() {
  const [d, setD] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getPjcAb().then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-sm text-slate-500">Carregando experimento…</div>;
  if (!d || !d.disponivel) return <div className="p-6 text-sm text-red-600">Painel indisponível{d?.erro ? `: ${d.erro}` : ''}.</div>;

  const A = d.controle, B = d.teste;
  const dec = d.decisao;
  const linhas: { label: string; a: string; b: string; inf?: React.ReactNode; tipo: 'primaria' | 'secundaria' | 'guardrail' | 'info' }[] = [
    { tipo: 'info', label: 'Orgs no braço', a: num(A.n), b: num(B.n) },
    { tipo: 'info', label: 'Orgs maduras (≥7d / ≥14d)', a: `${A.n_maduras_7} / ${A.n_maduras_14}`, b: `${B.n_maduras_7} / ${B.n_maduras_14}` },
    // ── PRIMÁRIA (08/10): créditos consumidos/org, orgs maduras ──────────────
    { tipo: 'primaria', label: 'Primária: créditos/org D+7 — média (mediana)',
      a: `${num(A.consumo7_por_org)} (${num(A.consumo7_mediana)})`, b: `${num(B.consumo7_por_org)} (${num(B.consumo7_mediana)})`,
      inf: A.consumo7_maduras === 0 && B.consumo7_maduras === 0
        ? <span className="text-xs text-slate-400">nenhuma org ≥7d ainda</span>
        : <BootInf b={d.inferencia.consumo7} /> },
    { tipo: 'primaria', label: 'Primária: créditos/org D+14 — média (mediana)',
      a: `${num(A.consumo14_por_org)} (${num(A.consumo14_mediana)})`, b: `${num(B.consumo14_por_org)} (${num(B.consumo14_mediana)})`,
      inf: A.consumo14_maduras === 0 && B.consumo14_maduras === 0
        ? <span className="text-xs text-slate-400">nenhuma org ≥14d ainda</span>
        : <BootInf b={d.inferencia.consumo14} /> },
    { tipo: 'secundaria', label: '% orgs que compram em D+14', a: `${pct(A.taxa_compra14)} (${A.compraram14}/${A.n_maduras_14})`, b: `${pct(B.taxa_compra14)} (${B.compraram14}/${B.n_maduras_14})`,
      inf: <Verdict p={d.inferencia.taxa_compra14.p_b_maior} /> },
    { tipo: 'secundaria', label: 'Receita total até agora', a: brl(A.receita_total_brl), b: brl(B.receita_total_brl) },
    { tipo: 'secundaria', label: '.pjc pagos / estornados (só teste)', a: '—', b: `${B.pjc_pagos} / ${B.pjc_estornos}` },
    { tipo: 'guardrail', label: 'Ativação D+7 (≥1 concluído)', a: `${pct(A.taxa_ativacao7)} (${A.ativadas7}/${A.n_maduras_7})`, b: `${pct(B.taxa_ativacao7)} (${B.ativadas7}/${B.n_maduras_7})`,
      inf: <Verdict p={d.inferencia.ativacao7.p_b_maior} /> },
    { tipo: 'guardrail', label: '.pjc baixado por caso concluído', a: `${pct(A.taxa_pjc_por_concluido)} (${A.casos_pjc_baixado}/${A.concluidos})`, b: `${pct(B.taxa_pjc_por_concluido)} (${B.casos_pjc_baixado}/${B.concluidos})`,
      inf: <Verdict p={d.inferencia.pjc_por_concluido.p_b_maior} /> },
    { tipo: 'guardrail', label: 'Estornos (todos)', a: num(A.estornos), b: num(B.estornos) },
    { tipo: 'guardrail', label: 'Pede humano no chat / msgs', a: `${A.pede_humano} / ${A.msgs_chat}`, b: `${B.pede_humano} / ${B.msgs_chat}` },
    { tipo: 'secundaria', label: 'Orgs que zeraram o saldo / dias até zerar (mediana)', a: `${pct(A.taxa_zerou)} (${A.zeraram}/${A.n}) · ${A.dias_ate_zero_mediana ?? '—'}d`, b: `${pct(B.taxa_zerou)} (${B.zeraram}/${B.n}) · ${B.dias_ate_zero_mediana ?? '—'}d`,
      inf: <Verdict p={d.inferencia.zerou?.p_b_maior ?? null} /> },
    { tipo: 'info', label: 'Modal .pjc — casos distintos: confirmou / cancelou / cancelou e nunca voltou', a: '—', b: `${B.casos_confirmou} / ${B.casos_cancelou} / ${B.casos_cancelou_sem_voltar}` },
    { tipo: 'info', label: 'Modal .pjc — orgs distintas: confirmou / cancelou', a: '—', b: `${B.orgs_confirmou} / ${B.orgs_cancelou}` },
    { tipo: 'info', label: 'Modal .pjc — cliques brutos: confirmou / cancelou / sem saldo', a: '—', b: `${B.ev_confirmou} / ${B.ev_cancelou} / ${B.ev_sem_saldo}` },
    { tipo: 'info', label: 'Casos / concluídos / PDF baixado', a: `${A.casos} / ${A.concluidos} / ${A.casos_pdf_baixado}`, b: `${B.casos} / ${B.concluidos} / ${B.casos_pdf_baixado}` },
  ];
  const tipoCls: Record<string, string> = { primaria: 'bg-indigo-50 text-indigo-700', secundaria: 'bg-sky-50 text-sky-700', guardrail: 'bg-amber-50 text-amber-700', info: 'bg-slate-50 text-slate-500' };

  // Receita: as duas visões (winsor p99 | sem top-1) sempre lado a lado, nas maduras D+14 e
  // em todas as orgs do braço. Se D+14 é ilegível, a seção madura não entra como "zero".
  const VIES = '(não-madura, viés)';
  const linhasReceita: { label: string; a: number | null; b: number | null; inf?: Boot | null }[] = [
    ...(d.inferencia.receita14.ilegivel ? [] : [
      { label: 'D+14, orgs maduras — bruta', a: A.receita14_por_org, b: B.receita14_por_org, inf: d.inferencia.receita14 },
      { label: 'D+14, orgs maduras — winsor p99', a: A.receita14_por_org_winsor, b: B.receita14_por_org_winsor, inf: d.inferencia.receita14.winsor },
      { label: 'D+14, orgs maduras — sem top-1', a: A.receita14_por_org_sem_top1, b: B.receita14_por_org_sem_top1, inf: d.inferencia.receita14.sem_top1 },
    ]),
    { label: `TODAS as orgs do braço — bruta ${VIES}`, a: A.receita_todas_por_org, b: B.receita_todas_por_org, inf: d.inferencia.receita_todas },
    { label: `TODAS as orgs do braço — winsor p99 ${VIES}`, a: A.receita_todas_por_org_winsor, b: B.receita_todas_por_org_winsor, inf: d.inferencia.receita_todas.winsor },
    { label: `TODAS as orgs do braço — sem top-1 ${VIES}`, a: A.receita_todas_por_org_sem_top1, b: B.receita_todas_por_org_sem_top1, inf: d.inferencia.receita_todas.sem_top1 },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800">A/B — .pjc cobrado à parte</h1>
        <p className="text-sm text-slate-500">
          Flip {new Date(d.flip_at).toLocaleString('pt-BR')} · {d.dias_desde_flip} dias · {d.orgs_por_dia ?? '—'} orgs novas/dia ·
          controle = .pjc incluso (como sempre) · teste = 1 crédito pelo .pjc, 1× por caso. Orgs antigas fora.
        </p>
      </div>

      {/* SRM */}
      <div className={`rounded-lg border px-4 py-3 text-sm ${d.srm.ok === false ? 'border-red-300 bg-red-50 text-red-800' : d.srm.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
        <span className="font-semibold">Sanidade (SRM):</span> controle {d.srm.n_a} × teste {d.srm.n_b}
        {d.srm.p != null && <> · χ² {d.srm.chi2} · p {d.srm.p}</>}
        {d.srm.ok === false && <> — <b>DESBALANCEADO: o teste está quebrado, ignore os resultados e investigue o sorteio.</b></>}
        {d.srm.ok === true && <> — balanceado.</>}
        {d.srm.ok === null && <> — sem orgs ainda.</>}
      </div>

      {/* DECISÃO — regra sequencial pré-registrada (08/10). O painel não decide: diz se PODE decidir. */}
      <div className={`rounded-lg border-2 p-4 ${dec.guardrail.estourado ? 'border-red-400 bg-red-50' : dec.pode_decidir ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 bg-slate-50'}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-bold text-slate-800">Decisão</h2>
          <span className="text-xs text-slate-500">
            leitura oficial <b>{new Date(`${dec.leitura_oficial}T00:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</b> · faltam {dec.dias_para_leitura_oficial} dias
          </span>
        </div>
        <p className={`mt-2 text-lg font-bold ${dec.guardrail.estourado ? 'text-red-700' : dec.pode_decidir ? 'text-emerald-700' : 'text-slate-700'}`}>
          {dec.estado}
        </p>
        <p className="mt-1 text-xs text-slate-500">{dec.regra}</p>

        <table className="mt-3 w-full text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="py-1 text-left font-medium">Critério de parada antecipada</th>
              <th className="py-1 text-right font-medium">perda esperada se escolher grátis</th>
              <th className="py-1 text-right font-medium">…se escolher cobrar</th>
              <th className="py-1 text-right font-medium">pior escolha</th>
              <th className="py-1 text-right font-medium">limiar</th>
              <th className="py-1 text-center font-medium">ok?</th>
            </tr>
          </thead>
          <tbody>
            {dec.criterios.map((c) => (
              <tr key={c.metrica} className="border-t border-slate-200">
                <td className="py-1">{c.metrica} <span className="text-slate-400">({c.unidade})</span></td>
                <td className="py-1 text-right tabular-nums">{num(c.expected_loss_a)}</td>
                <td className="py-1 text-right tabular-nums">{num(c.expected_loss_b)}</td>
                <td className="py-1 text-right tabular-nums font-semibold">{num(c.expected_loss_pior)}</td>
                <td className="py-1 text-right tabular-nums text-slate-500">{c.limiar}</td>
                <td className={`py-1 text-center font-bold ${c.atingido ? 'text-emerald-700' : 'text-slate-400'}`}>{c.atingido ? '✓' : '✗'}</td>
              </tr>
            ))}
            <tr className={`border-t border-slate-200 ${dec.guardrail.estourado ? 'text-red-700' : ''}`}>
              <td className="py-1">Guardrail — {dec.guardrail.metrica} <span className="text-slate-400">(tolerância {dec.guardrail.tolerancia})</span></td>
              <td className="py-1 text-right tabular-nums">{pct(dec.guardrail.controle)}</td>
              <td className="py-1 text-right tabular-nums">{pct(dec.guardrail.teste)}</td>
              <td className="py-1 text-right tabular-nums font-semibold">{pctRel(dec.guardrail.queda_relativa)} rel.</td>
              <td className="py-1 text-right tabular-nums text-slate-500">P(ctrl&gt;teste) {pct(dec.guardrail.p_controle_maior, 0)}</td>
              <td className={`py-1 text-center font-bold ${dec.guardrail.estourado ? 'text-red-700' : 'text-emerald-700'}`}>{dec.guardrail.estourado ? 'ESTOUROU' : '✓'}</td>
            </tr>
          </tbody>
        </table>
        {dec.nota_regra && (
          <p className="mt-2 text-xs text-slate-500">
            {dec.nota_regra} Hoje ela diria: <b>{dec.criterio_lider_atingido ? 'pararia' : 'também não pararia'}</b>.
          </p>
        )}
        <p className="mt-2 text-xs text-amber-800"><b>⚠️ {dec.aviso_receita}</b></p>
        <p className="mt-1 text-xs text-slate-500">{dec.decisao_final}</p>
      </div>

      {/* Tabela principal */}
      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="px-3 py-2 text-left">Métrica</th><th className="px-3 py-2 text-right">Controle</th><th className="px-3 py-2 text-right">Teste</th><th className="px-3 py-2 text-left">Inferência</th></tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.label} className="border-t border-slate-100">
                <td className="px-3 py-2"><span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${tipoCls[l.tipo]}`}>{l.tipo}</span>{l.label}</td>
                <td className="px-3 py-2 text-right tabular-nums">{l.a}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{l.b}</td>
                <td className="px-3 py-2">{l.inf ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* RECEITA — secundária desde 08/10. Sempre as duas visões lado a lado; sem comprador
          maduro a célula diz ILEGÍVEL em vez de fingir um IC [0;0]. */}
      <div className="rounded-lg border border-slate-200 p-4 text-sm">
        <h2 className="font-semibold text-slate-700">Receita <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-700">secundária</span></h2>
        <p className="mt-1 text-xs text-slate-500">
          Cauda pesada e n minúsculo ⇒ nunca uma visão só: <b>winsor p99</b> (capa o extremo) e <b>sem top-1</b> (e se a melhor org do braço não existisse?) sempre juntas.
        </p>

        {d.inferencia.receita14.ilegivel ? (
          <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <b>Receita/org D+14 (orgs maduras): ILEGÍVEL</b> — compradores maduros = {A.compradores_maduros14 + B.compradores_maduros14} (controle {A.compradores_maduros14} · teste {B.compradores_maduros14}),
            com {A.n_maduras_14}/{B.n_maduras_14} orgs maduras. {d.inferencia.receita14.motivo}
          </div>
        ) : null}

        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Visão</th>
                <th className="px-2 py-1 text-right font-medium">Controle</th>
                <th className="px-2 py-1 text-right font-medium">Teste</th>
                <th className="px-2 py-1 text-left font-medium">Inferência</th>
              </tr>
            </thead>
            <tbody>
              {linhasReceita.map((l) => (
                <tr key={l.label} className="border-t border-slate-100">
                  <td className="px-2 py-1">{l.label}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{brl(l.a)}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-semibold">{brl(l.b)}</td>
                  <td className="px-2 py-1"><BootInf b={l.inf} fmt={brl} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          "não-madura, viés": inclui coortes que tiveram menos de 14 dias para comprar, então subestima as duas pontas — serve para não jogar fora 2/3 das orgs, não para decidir.
        </p>

        {/* Compradores nominais: é aqui que se vê a instabilidade do sinal de receita. */}
        <h3 className="mt-4 font-semibold text-slate-700">
          Compradores por braço — {A.compraram_total} controle × {B.compraram_total} teste
          <span className="ml-2 font-normal text-xs text-amber-800">⚠️ {dec.aviso_receita}</span>
        </h3>
        <div className="mt-2 grid gap-4 md:grid-cols-2">
          {([['controle', A.compraram_total, d.compradores.controle], ['teste', B.compraram_total, d.compradores.teste]] as const).map(([nome, tot, lista]) => (
            <div key={nome} className="rounded border border-slate-200">
              <div className="border-b border-slate-100 bg-slate-50 px-2 py-1 text-xs font-semibold uppercase text-slate-500">{nome} ({tot})</div>
              {lista.length === 0 ? <p className="px-2 py-2 text-xs text-slate-400">Nenhum comprador.</p> : (
                <table className="w-full text-xs">
                  <tbody>
                    {lista.map((c) => (
                      <tr key={c.org} className="border-t border-slate-100">
                        <td className="px-2 py-1">org {c.org}</td>
                        <td className="px-2 py-1 text-slate-500">{String(c.data).slice(0, 10).split('-').reverse().join('/')}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{brl(c.brl)}</td>
                        <td className="px-2 py-1 text-right text-slate-400">{c.n_compras}× · {c.madura_14 ? 'madura' : 'imatura'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Poder */}
        <div className="rounded-lg border border-slate-200 p-4 text-sm">
          <h2 className="font-semibold text-slate-700 mb-2">Poder estatístico (o que dá para afirmar)</h2>
          <ul className="space-y-1 text-slate-600">
            <li>Baseline taxa de compra D+14: <b>{pct(d.poder.baseline_taxa_compra)}</b> (controle; 10% histórico até haver maduras)</li>
            <li>Com {d.poder.n_maduras_min} maduras/braço, o menor efeito detectável hoje é <b>{d.poder.mde_abs_agora == null ? '—' : `±${pct(d.poder.mde_abs_agora)} abs (${pct(d.poder.mde_rel_agora, 0)} rel.)`}</b></li>
            <li>Para ler ±25% relativo (80% poder, α 5%): <b>{num(d.poder.n_alvo_por_braco)} orgs maduras por braço</b> ≈ {d.poder.dias_estimados_para_alvo == null ? '—' : `${d.poder.dias_estimados_para_alvo} dias`} no ritmo atual (+14 de maturação)</li>
          </ul>
          <p className="mt-2 text-xs text-slate-400">Antes disso, só um efeito grande (ex.: conversão caindo pela metade, ativação −20%) é legível — e é para isso que servem os guardrails.</p>
        </div>

        {/* Pré-registro */}
        <div className="rounded-lg border border-slate-200 p-4 text-sm">
          <h2 className="font-semibold text-slate-700 mb-2">Pré-registro (não muda depois de olhar)</h2>
          <ul className="space-y-1 text-slate-600">
            {d.pre_registro.decisao && <li className="font-semibold text-amber-700"><b>Decisão:</b> {d.pre_registro.decisao}</li>}
            <li><b>Unidade:</b> {d.pre_registro.unidade}</li>
            <li><b>Primária:</b> {d.pre_registro.primaria}</li>
            {d.pre_registro.primaria_trocada_em && <li className="text-xs text-slate-500">Troca de primária: {d.pre_registro.primaria_trocada_em}</li>}
            <li><b>Secundárias:</b> {d.pre_registro.secundarias.join(' · ')}</li>
            <li><b>Guardrails:</b> {d.pre_registro.guardrails.join(' · ')}</li>
            <li><b>Sanidade:</b> {d.pre_registro.sanidade}</li>
            <li><b>Parada:</b> {d.pre_registro.regra_de_parada}</li>
          </ul>
        </div>
      </div>

      {/* Atribuição (balanço de canais entre braços) */}
      <div className="rounded-lg border border-slate-200 p-4 text-sm">
        <h2 className="font-semibold text-slate-700 mb-2">Origem das orgs por braço (deve ser parecida — randomização por org)</h2>
        {d.atribuicao.length === 0 ? <p className="text-slate-400 text-xs">Sem orgs ainda.</p> : (
          <table className="text-xs"><tbody>
            {d.atribuicao.map((a, i) => (
              <tr key={i}><td className="pr-4 py-0.5">{a.pjc_cobrado ? 'teste' : 'controle'}</td><td className="pr-4 py-0.5">{a.source}</td><td className="py-0.5 tabular-nums">{a.n}</td></tr>
            ))}
          </tbody></table>
        )}
      </div>

      {/* Orgs */}
      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 uppercase text-slate-500">
            <tr>{['org', 'braço', 'cadastro', 'casos', 'concl.', 'PDF dl', '.pjc dl', '.pjc pagos', 'compras', 'receita', 'estornos', 'pede humano'].map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr>
          </thead>
          <tbody>
            {d.orgs.map((o) => (
              <tr key={o.id} className="border-t border-slate-100">
                <td className="px-2 py-1">{o.id}</td>
                <td className="px-2 py-1">{o.pjc_cobrado ? <span className="text-indigo-700 font-semibold">teste</span> : 'controle'}</td>
                <td className="px-2 py-1">{String(o.created_at).slice(0, 16).replace('T', ' ')}</td>
                <td className="px-2 py-1">{o.n_casos}</td><td className="px-2 py-1">{o.n_concluidos}</td>
                <td className="px-2 py-1">{o.casos_pdf_baixado}</td><td className="px-2 py-1">{o.casos_pjc_baixado}</td>
                <td className="px-2 py-1">{o.pjc_pagos}</td><td className="px-2 py-1">{o.n_compras}</td>
                <td className="px-2 py-1">{brl(Number(o.rev_total) / 100)}</td>
                <td className="px-2 py-1">{o.estornos}</td><td className="px-2 py-1">{o.pede_humano}</td>
              </tr>
            ))}
            {d.orgs.length === 0 && <tr><td colSpan={12} className="px-2 py-3 text-slate-400">Nenhuma org nova desde o flip.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
